import { useI18n, useMoneyFormatter } from "@expertloop/i18n/react";
import type { AnalyticsMarketplaceStats } from "@expertloop/shared";

function Tile({ label, value }: { label: string; value: string }) {
	return (
		<div className="admin-overview__tile">
			<span className="admin-overview__tile-label">{label}</span>
			<strong className="admin-overview__tile-value">{value}</strong>
		</div>
	);
}

/**
 * Totaux financiers, une carte par devise.
 *
 * Chaque valeur absente reste absente. Le taux de commission et le panier
 * moyen ne s'affichent que lorsque le serveur a pu les calculer, et les
 * commandes dont un chiffre a dû être reconstitué sont annoncées sous la
 * carte : sans cette mention, un total exact et un total reconstitué se
 * ressembleraient trait pour trait.
 */
export function CurrencyTotals({ marketplace }: { marketplace: AnalyticsMarketplaceStats }) {
	const { t } = useI18n();
	const { formatCents } = useMoneyFormatter();

	if (marketplace.byCurrency.length === 0) {
		return <p className="admin-overview__state">{t("adminOverview.empty")}</p>;
	}

	return (
		<div className="admin-overview__currencies">
			{marketplace.byCurrency.map((row) => {
				const refunds = marketplace.refundsByCurrency.find((entry) => entry.currency === row.currency);
				const authorized = marketplace.authorizedByCurrency.find((entry) => entry.currency === row.currency);

				return (
					<div key={row.currency} className="admin-overview__currency">
						<h3 className="admin-overview__currency-title">{row.currency.toUpperCase()}</h3>
						<div className="admin-overview__tiles">
							<Tile label={t("adminFinance.gmv")} value={formatCents(row.gmv, row.currency)} />
							<Tile label={t("adminFinance.platformFee")} value={formatCents(row.platformFee, row.currency)} />
							<Tile label={t("adminFinance.stripeFee")} value={formatCents(row.stripeFee, row.currency)} />
							<Tile label={t("adminFinance.captureCount")} value={String(row.orderCount)} />
							{row.effectiveFeeRate !== null && (
								<Tile
									label={t("adminFinance.effectiveFeeRate")}
									value={`${(row.effectiveFeeRate * 100).toFixed(2)} %`}
								/>
							)}
							{row.averageOrderValue !== null && (
								<Tile
									label={t("adminFinance.averageOrderValue")}
									value={formatCents(row.averageOrderValue, row.currency)}
								/>
							)}
							{authorized && (
								<Tile
									label={t("adminFinance.authorized")}
									value={formatCents(authorized.authorizedAmount, row.currency)}
								/>
							)}
							{refunds && (
								<>
									<Tile
										label={t("adminFinance.refunded")}
										value={formatCents(refunds.refundedAmount, row.currency)}
									/>
									<Tile label={t("adminFinance.refundCount")} value={String(refunds.refundCount)} />
									<Tile
										label={t("adminFinance.feeReturned")}
										value={formatCents(refunds.platformFeeReturned, row.currency)}
									/>
									<Tile
										label={t("adminFinance.feeRetained")}
										value={formatCents(refunds.stripeFeeRetained, row.currency)}
									/>
								</>
							)}
						</div>
						{row.ordersMissingPlatformFeeAmount > 0 && (
							<p className="admin-overview__caveat">
								{t("adminFinance.feeFallbackNotice", {
									count: row.ordersMissingPlatformFeeAmount,
									plural: row.ordersMissingPlatformFeeAmount !== 1 ? "s" : "",
								})}
							</p>
						)}
						{refunds && refunds.refundsMissingFeeReturned > 0 && (
							<p className="admin-overview__caveat">
								{t("adminFinance.feeReturnedFallbackNotice", {
									count: refunds.refundsMissingFeeReturned,
									plural: refunds.refundsMissingFeeReturned !== 1 ? "s" : "",
								})}
							</p>
						)}
						{row.ordersMissingStripeFee > 0 && (
							<p className="admin-overview__caveat">
								{t("adminFinance.stripeFeeMissingNotice", {
									count: row.ordersMissingStripeFee,
									plural: row.ordersMissingStripeFee !== 1 ? "s" : "",
								})}
							</p>
						)}
					</div>
				);
			})}
		</div>
	);
}
